/**
 * @fileoverview Quota Context for managing quota state across the application
 * Provides quota information and state management for quota exceeded scenarios
 */

import React, { createContext, useContext, useReducer } from 'react';

/**
 * @typedef {Object} QuotaState
 * @property {import('../lib/quota.js').QuotaInfo|null} lastQuota - Last quota error information
 * @property {boolean} isModalOpen - Whether the quota modal is currently open
 * @property {boolean} isBannerVisible - Whether the quota banner should be visible
 * @property {boolean} isBannerDismissed - Whether the user has dismissed the banner
 */

/**
 * Initial state for quota context
 * @type {QuotaState}
 */
const initialState = {
  lastQuota: null,
  isModalOpen: false,
  isBannerVisible: false,
  isBannerDismissed: false
};

/**
 * Quota actions
 */
const QUOTA_ACTIONS = {
  SET_QUOTA: 'SET_QUOTA',
  CLEAR_QUOTA: 'CLEAR_QUOTA',
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  SHOW_BANNER: 'SHOW_BANNER',
  HIDE_BANNER: 'HIDE_BANNER',
  DISMISS_BANNER: 'DISMISS_BANNER'
};

/**
 * Quota reducer function
 * @param {QuotaState} state - Current state
 * @param {Object} action - Action object
 * @returns {QuotaState} New state
 */
function quotaReducer(state, action) {
  switch (action.type) {
    case QUOTA_ACTIONS.SET_QUOTA:
      return {
        ...state,
        lastQuota: action.payload,
        isModalOpen: true, // Auto-open modal when quota is set
        isBannerVisible: action.payload && action.payload.remaining === 0,
        isBannerDismissed: false
      };
      
    case QUOTA_ACTIONS.CLEAR_QUOTA:
      return {
        ...state,
        lastQuota: null,
        isModalOpen: false,
        isBannerVisible: false,
        isBannerDismissed: false
      };
      
    case QUOTA_ACTIONS.OPEN_MODAL:
      return {
        ...state,
        isModalOpen: true
      };
      
    case QUOTA_ACTIONS.CLOSE_MODAL:
      return {
        ...state,
        isModalOpen: false
      };
      
    case QUOTA_ACTIONS.SHOW_BANNER:
      return {
        ...state,
        isBannerVisible: true,
        isBannerDismissed: false
      };
      
    case QUOTA_ACTIONS.HIDE_BANNER:
      return {
        ...state,
        isBannerVisible: false
      };
      
    case QUOTA_ACTIONS.DISMISS_BANNER:
      return {
        ...state,
        isBannerVisible: false,
        isBannerDismissed: true
      };
      
    default:
      return state;
  }
}

/**
 * Quota Context
 */
const QuotaContext = createContext(undefined);

/**
 * Quota Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function QuotaProvider({ children }) {
  const [state, dispatch] = useReducer(quotaReducer, initialState);

  /**
   * Set quota information (usually from a quota exceeded error)
   * @param {import('../lib/quota.js').QuotaInfo} quotaInfo - Quota information
   */
  const setQuota = (quotaInfo) => {
    dispatch({ type: QUOTA_ACTIONS.SET_QUOTA, payload: quotaInfo });
  };

  /**
   * Clear quota information and hide all UI
   */
  const clearQuota = () => {
    dispatch({ type: QUOTA_ACTIONS.CLEAR_QUOTA });
  };

  /**
   * Open the quota modal
   */
  const openModal = () => {
    dispatch({ type: QUOTA_ACTIONS.OPEN_MODAL });
  };

  /**
   * Close the quota modal
   */
  const closeModal = () => {
    dispatch({ type: QUOTA_ACTIONS.CLOSE_MODAL });
  };

  /**
   * Show the quota banner
   */
  const showBanner = () => {
    dispatch({ type: QUOTA_ACTIONS.SHOW_BANNER });
  };

  /**
   * Hide the quota banner
   */
  const hideBanner = () => {
    dispatch({ type: QUOTA_ACTIONS.HIDE_BANNER });
  };

  /**
   * Dismiss the quota banner (user action)
   */
  const dismissBanner = () => {
    dispatch({ type: QUOTA_ACTIONS.DISMISS_BANNER });
  };

  /**
   * Check if actions should be disabled due to quota
   * @param {string} [scope] - Optional scope to check (if not provided, checks general quota)
   * @returns {boolean} True if actions should be disabled
   */
  const isQuotaExhausted = (scope) => {
    if (!state.lastQuota) return false;
    
    // If scope is specified, check if it matches
    if (scope && state.lastQuota.scope && state.lastQuota.scope !== scope) {
      return false;
    }
    
    return state.lastQuota.remaining === 0;
  };

  /**
   * Get quota information for a specific scope
   * @param {string} [scope] - Optional scope to get quota for
   * @returns {import('../lib/quota.js').QuotaInfo|null} Quota information or null
   */
  const getQuotaForScope = (scope) => {
    if (!state.lastQuota) return null;
    
    // If no scope specified or scope matches, return the quota
    if (!scope || !state.lastQuota.scope || state.lastQuota.scope === scope) {
      return state.lastQuota;
    }
    
    return null;
  };

  const value = {
    // State
    ...state,
    
    // Actions
    setQuota,
    clearQuota,
    openModal,
    closeModal,
    showBanner,
    hideBanner,
    dismissBanner,
    
    // Utility functions
    isQuotaExhausted,
    getQuotaForScope
  };

  return (
    <QuotaContext.Provider value={value}>
      {children}
    </QuotaContext.Provider>
  );
}

/**
 * Hook to use quota context
 * @returns {Object} Quota context value
 * @throws {Error} If used outside of QuotaProvider
 */
export function useQuota() {
  const context = useContext(QuotaContext);
  
  if (context === undefined) {
    throw new Error('useQuota must be used within a QuotaProvider');
  }
  
  return context;
}

/**
 * HOC to wrap components with quota functionality
 * @param {React.Component} WrappedComponent - Component to wrap
 * @returns {React.Component} Enhanced component with quota props
 */
export function withQuota(WrappedComponent) {
  return function QuotaEnhancedComponent(props) {
    const quota = useQuota();
    return <WrappedComponent {...props} quota={quota} />;
  };
}

export default QuotaContext;
